/**
 * Token Expiration Monitor Component
 *
 * This component monitors token expiration and proactively refreshes tokens
 * before they expire to provide a seamless user experience.
 *
 * Features:
 * - Checks token expiration every minute
 * - Proactively refreshes token 5 minutes before expiration
 * - Shows warning notification 2 minutes before expiration
 * - Handles refresh failures gracefully
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isTokenExpired, getValidAccessToken } from '../services/msalService';
import { toast } from 'react-toastify';

const TokenMonitor = () => {
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const hasShownWarning = useRef(false);

    useEffect(() => {
        // Only monitor tokens for logged-in users in approval portal
        const isLoggedIn = localStorage.getItem('isLoggedin') === 'true';
        const token = localStorage.getItem('accessToken');

        if (!isLoggedIn || !token) {
            return;
        }

        /**
         * Check token expiration and refresh if needed
         */
        const checkAndRefreshToken = async () => {
            const currentToken = localStorage.getItem('accessToken');

            if (!currentToken) {
                // No token, user might have logged out
                return;
            }

            try {
                // Check if token is expired or will expire in next 5 minutes (300 seconds)
                const willExpireSoon = isTokenExpired(currentToken, 300);

                // Check if token will expire in next 2 minutes (120 seconds) - for warning
                const willExpireVeryNoon = isTokenExpired(currentToken, 120);

                if (willExpireSoon) {
                    console.log('Token expiring soon, attempting refresh...');

                    // Show warning if token will expire in less than 2 minutes
                    if (willExpireVeryNoon && !hasShownWarning.current) {
                        toast.warning('Your session will expire soon. Refreshing...', {
                            position: 'top-right',
                            autoClose: 3000,
                        });
                        hasShownWarning.current = true;
                    }

                    // Attempt to get a valid token (will refresh automatically)
                    const newToken = await getValidAccessToken();

                    if (newToken) {
                        console.log('Token refreshed successfully');
                        hasShownWarning.current = false; // Reset warning flag

                        // Optional: Show success message
                        // toast.success('Session refreshed successfully', {
                        //     position: 'top-right',
                        //     autoClose: 2000,
                        // });
                    } else {
                        // Token refresh failed
                        console.error('Token refresh failed');
                        toast.error('Session expired. Please log in again.', {
                            position: 'top-right',
                            autoClose: 5000,
                        });

                        // Clear auth data and redirect
                        localStorage.clear();
                        localStorage.setItem('isLoggedin', 'false');
                        navigate('/approval');
                    }
                }
            } catch (error) {
                console.error('Error checking token expiration:', error);
            }
        };

        // Check immediately on mount
        checkAndRefreshToken();

        // Set up interval to check every 60 seconds
        intervalRef.current = setInterval(checkAndRefreshToken, 60000); // 60 seconds

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [navigate]);

    // This component doesn't render anything
    return null;
};

export default TokenMonitor;
